import { Title } from "../components/Title"
import { Button } from "../components/Button"
import { useModel } from "../hooks/useModel"
import { useDefinedParams } from "../hooks/useDefinedParams"
import { getContractsSuccessType, getContractsErrorType } from "../../../APIDocs/apiTypes"
import { useJbpQuery, RestTypes } from "../hooks/useJbpQuery"
import { ContractsLoading } from "./loadingElements/ContractsLoading"
import { ExternalLink } from "../components/ExternalLink"
import { Typography } from "../components/Typography"
import { EnvironmentVariables } from "../EnvironmentVariables"
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useNav, useNavToRoot, useWasNavigated } from "../hooks/useNav";
import { FormProvider, useForm } from "react-hook-form"
import { FormSearchDropdown } from "@portal_components/components/FormSearchDropdown"
import { FormButton } from "@portal_components/components/FormButton"

type ContractFormValues = {
    contractId: string;
}

export const Contract = () => {
    const model = useModel();
    const nav = useNavigate();
    useEffect(() => {
        if(useWasNavigated() === false) {
            useNavToRoot(nav);
        }
    }, []);

    const portfolioLink = EnvironmentVariables.PortfolioLink;
    const [mpan, serviceLine] = useDefinedParams(["mpan", "serviceLine"]);
    // const [continueDisabled, setContinueDisabled] = useState(true);
    // const selectedContract = useSynchronousState<getContractsSuccessType[number] | undefined>(undefined);

    const onContractsLoaded = (contracts: getContractsSuccessType) => {
        console.log("Contracts loaded:", contracts);
        if(contracts.length === 1) {
            model.Set("model.contractId", contracts[0].ContractId);
            model.Set("model.contractCustomerName", contracts[0].CustomerName);
            model.Set("model.contractDisplayName", getContractDisplayName(contracts[0]));
            useNav(`/site/${mpan}/${serviceLine}`, nav);
        }
    }

    const { data, isLoading: contractsLoading } = useJbpQuery<getContractsSuccessType, getContractsErrorType>(
        RestTypes.Get,
        undefined,
        ['contract-options'],
        `jbp/${mpan}/contract-options`,
        true,
        serviceLine,
        onContractsLoaded,
        undefined
    );

    useEffect(() => {
        if(!data) {
            console.log("Data is undefined, skipping useEffect logic.");
            return;
        }

        const url = sessionStorage.getItem("lastAttemptedUrl");
        if(url === null) {
            return;
        }

        sessionStorage.removeItem('lastAttemptedUrl');
        const parts = url.split('/');
        const mpanFromReferrer = parts[2];

        const attemptedContractId = mpanFromReferrer.split('-')[0];

        if(attemptedContractId !== null) {
            const contract = data.find(contract => contract.ContractId === attemptedContractId);
            if(contract) {
                setContractOnModelAndNavigate(contract);
            }
        }
    }, [data]);

    const routeToSearchScreen = () => {
        useNavToRoot(nav);
    }

    const getContractDisplayName = (contract: getContractsSuccessType[number] | undefined): string => {
        if(contract === undefined) return "";
        const displaySiteEffectiveFrom = contract.EffectiveDate != null ? `, Site Effective From: ${contract.EffectiveDate}` : "";
        return `${contract.CustomerName} (Contract ID: ${contract.ContractId}${displaySiteEffectiveFrom})`
    }

    const handleSubmit = (formData: ContractFormValues) => {
        if(!data) return;
        const contract = data.find(contract => contract.ContractId === formData.contractId);
        if(!contract) return;
        setContractOnModelAndNavigate(contract);
    }

    const setContractOnModelAndNavigate = (contract: getContractsSuccessType[number]) => {
        // this would be way better using redux
        model.Set("model.contractId", contract.ContractId);
        model.Set("model.contractCustomerName", contract.CustomerName);
        model.Set("model.contractDisplayName", getContractDisplayName(contract));
        useNav(`/site/${mpan}/${serviceLine}`, nav);
    }

    const methods = useForm<ContractFormValues>({})
    const selectedContractId = methods.watch("contractId");

    if(data?.length === 1) return null;

    return (
        <div id="jbp-contracts-screen">
            <div className='w-1/2 inline-block align-top'>
                <div className='ml-10'>
                    <Title className="mt-7 mb-0" text={`MPAN: ${mpan}`} />
                    {contractsLoading ?
                        <div>
                            <ContractsLoading className="mt-2 w-1/3" />
                        </div>
                        :
                        !data || data?.length === 0 ?
                            <div>
                                <Typography className="inline-block mr-1 pt-5" content={`No ${serviceLine} Contracts found for this MPAN. If you wish to associate a contract to this Site, please use the `}></Typography>
                                <ExternalLink className="inline-block" link={portfolioLink} linkText="Stark Portfolio Portal" openInNewTab={true} />
                                <br></br>
                                <Button text="Back to Search Screen" primary={true} onClick={routeToSearchScreen} className="mt-2" widthStyling="w-2/8" disabled={false} />
                            </div>
                            :
                            <div className="max-w-[30em]">
                                <Title className="mt-7 mb-4" text="Contract ID:" />
                                <FormProvider {...methods}>
                                    <form onSubmit={methods.handleSubmit(handleSubmit)}>
                                        <FormSearchDropdown<ContractFormValues>
                                            name="contractId"
                                            title="Select Contract"
                                            elementsForDisplay={data?.map(contract => ({ title: getContractDisplayName(contract), value: contract.ContractId }))}
                                            placeholder="Select Contract..."
                                        />
                                        <FormButton type="submit" primary icon disabled={!selectedContractId}>
                                            Continue
                                        </FormButton>
                                    </form>
                                </FormProvider>
                            </div>
                    }
                </div>
            </div>
            <div className="inline-block w-1/2 h-screen bg-center bg-origin-content bg-cover" style={{ backgroundImage: "url(/searchImagery.png)" }}></div>
        </div>
    )
}
